import { useEffect, useMemo, useState } from "react";
import { useLocation } from "wouter";
import { format } from "date-fns";
import {
  ChevronDown,
  ChevronRight,
  Pencil,
  Ban,
  HelpCircle,
  FileText,
  Loader2,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { CommissionInquiryDialog } from "@/components/invoicing/CommissionInquiryDialog";
import { VoidInvoiceConfirmDescription } from "@/components/invoicing/VoidInvoiceConfirmDescription";
import type { Invoice } from "@shared/schema";
import {
  InvoiceLockedByClaimError,
  useVoidInvoice,
} from "@/hooks/useInvoices";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/context/AuthContext";
import { useImpersonation } from "@/context/ImpersonationContext";
import { FINANCES_PATHS } from "@/lib/financesPaths";
import { isInvoiceTramsSyncError } from "@shared/schema";
import {
  formatTramsInvoiceDisplayNumber,
  getTramsInvoiceNumberFromMetadata,
} from "@shared/invoice-display";

const invoiceStatusConfig: Record<string, { label: string; color: string }> = {
  O: { label: "Open", color: "text-[#3b82f6]" },
  C: { label: "Closed", color: "text-[#10b981]" },
  V: { label: "Voided", color: "text-gray-400" },
};

function parseDateAsLocal(value: string | Date | null | undefined): string {
  if (!value) return "—";
  const str = typeof value === "string" ? value : value.toISOString();
  const datePart = str.substring(0, 10);
  return format(new Date(`${datePart}T00:00:00`), "MM/dd/yyyy");
}

function formatMoney(value: string | null | undefined): string {
  return `$${parseFloat(value || "0").toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

interface TripInvoiceListProps {
  tripId: number;
  invoices: Invoice[];
  isLoading?: boolean;
  highlightInvoiceId?: number;
}

export default function TripInvoiceList({
  tripId,
  invoices,
  isLoading,
  highlightInvoiceId,
}: TripInvoiceListProps) {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const { user } = useAuth();
  const { impersonation } = useImpersonation();
  const voidInvoice = useVoidInvoice();

  const effectiveUserId =
    impersonation.active && impersonation.targetUserId
      ? impersonation.targetUserId
      : user?.id;

  const sortedInvoices = useMemo(
    () =>
      [...invoices].sort((a, b) => {
        const aDate = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const bDate = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return bDate - aDate;
      }),
    [invoices],
  );

  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());
  const [voidTarget, setVoidTarget] = useState<Invoice | null>(null);
  const [voidReason, setVoidReason] = useState("");
  const [inquiryInvoiceId, setInquiryInvoiceId] = useState<number | null>(null);

  useEffect(() => {
    if (!highlightInvoiceId) return;
    if (!invoices.some((invoice) => invoice.id === highlightInvoiceId)) return;
    setExpandedIds(new Set([highlightInvoiceId]));
  }, [highlightInvoiceId, invoices]);

  const toggleExpanded = (id: number) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleVoidConfirm = async () => {
    if (!voidTarget) return;
    try {
      await voidInvoice.mutateAsync({
        id: voidTarget.id,
        reason: voidReason.trim() || undefined,
      });
      toast({
        title: "Void request submitted",
      });
      setVoidTarget(null);
      setVoidReason("");
    } catch (error: unknown) {
      if (error instanceof InvoiceLockedByClaimError) {
        toast({
          title: "Cannot void this invoice",
          description: `Linked to unclaimed-commission claim #${error.claimId ?? "?"}.`,
          variant: "destructive",
        });
        return;
      }
      toast({
        title: "Void failed",
        description: error instanceof Error ? error.message : "Please try again.",
        variant: "destructive",
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin mr-2" />
        Loading invoices...
      </div>
    );
  }

  if (sortedInvoices.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <FileText className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">
            No invoices on this trip yet.
          </p>
          <Button
            className="mt-4"
            onClick={() => setLocation(`${FINANCES_PATHS.invoicesNew}?tripId=${tripId}`)}
          >
            Add Invoice
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <div className="space-y-3">
        <div className="flex items-center justify-between px-1">
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
            Invoices ({sortedInvoices.length})
          </h3>
        </div>

        {sortedInvoices.map((invoice) => {
          const isExpanded = expandedIds.has(invoice.id);
          const isOpen = invoice.invoiceStatusCode === "O";
          const isCreator = effectiveUserId === invoice.createdBy;
          const isPendingVoid = invoice.status === "voided" && isOpen;
          const status = invoiceStatusConfig[invoice.invoiceStatusCode ?? "O"];
          const syncError = isInvoiceTramsSyncError(invoice.syncStatus)
            ? { label: "TRAMS Sync Failed", variant: "destructive" as const }
            : null;
          const editAllowed = isOpen && isCreator && !isPendingVoid;
          const voidAllowed = isOpen && isCreator && !isPendingVoid;
          const tramsInvoiceNumber = getTramsInvoiceNumberFromMetadata(invoice.metadata);

          return (
            <Card
              key={invoice.id}
              className={highlightInvoiceId === invoice.id ? "ring-2 ring-primary" : undefined}
            >
              <CardContent className="p-0">
                <button
                  type="button"
                  className="w-full flex items-center gap-3 p-4 text-left hover:bg-muted/40 transition-colors"
                  onClick={() => toggleExpanded(invoice.id)}
                >
                  {isExpanded ? (
                    <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
                  ) : (
                    <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium">
                        {invoice.supplierName || `Invoice #${formatTramsInvoiceDisplayNumber(tramsInvoiceNumber)}`}
                      </span>
                      <span className="text-sm text-muted-foreground">
                        #{formatTramsInvoiceDisplayNumber(tramsInvoiceNumber)}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground truncate">
                      {invoice.primaryTraveler || "—"}
                      {" · "}
                      {formatMoney(invoice.totalFare)}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className={`text-sm font-medium ${status?.color ?? ""}`}>
                      {status?.label ?? invoice.invoiceStatusCode}
                    </span>
                    {syncError && (
                      <Badge variant={syncError.variant} className="text-xs">
                        {syncError.label}
                      </Badge>
                    )}
                  </div>
                </button>

                {isExpanded && (
                  <div className="border-t px-4 pb-4 pt-3 space-y-4">
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                      <div>
                        <p className="text-muted-foreground">Traveler</p>
                        <p className="font-medium">{invoice.primaryTraveler || "—"}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Confirmation #</p>
                        <p className="font-medium">{invoice.confirmationNumber || "—"}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Travel Type</p>
                        <p className="font-medium">{invoice.travelType || "—"}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Departure</p>
                        <p className="font-medium">{parseDateAsLocal(invoice.departureDate)}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Return</p>
                        <p className="font-medium">{parseDateAsLocal(invoice.returnDate)}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Commission</p>
                        <p className="font-medium">{formatMoney(invoice.commissionAmount)}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Total Sale Amount</p>
                        <p className="font-medium">{formatMoney(invoice.totalFare)}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Created</p>
                        <p className="font-medium">
                          {invoice.createdAt
                            ? format(new Date(invoice.createdAt), "MM/dd/yyyy")
                            : "—"}
                        </p>
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-2">
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="inline-flex">
                              <Button
                                variant="outline"
                                size="sm"
                                disabled={!editAllowed}
                                onClick={() =>
                                  setLocation(FINANCES_PATHS.invoiceEdit(invoice.id))
                                }
                              >
                                <Pencil className="h-3.5 w-3.5 mr-1.5" />
                                Edit
                              </Button>
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>
                            {!isOpen
                              ? "Only Open invoices can be edited"
                              : !isCreator
                                ? "Only the invoice creator can edit"
                                : isPendingVoid
                                  ? "This invoice cannot be edited while a void request is in progress"
                                  : "Edit invoice"}
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>

                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="inline-flex">
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-destructive hover:text-destructive"
                                disabled={!voidAllowed}
                                onClick={() => setVoidTarget(invoice)}
                              >
                                <Ban className="h-3.5 w-3.5 mr-1.5" />
                                Void
                              </Button>
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>
                            {!isOpen
                              ? "Only Open invoices can be voided"
                              : !isCreator
                                ? "Only the invoice creator can void"
                                : isPendingVoid
                                  ? "A void request is already in progress for this invoice"
                                  : "Void invoice"}
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>

                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="inline-flex">
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-amber-600 hover:text-amber-700"
                                aria-label={`Open commission inquiry for invoice ${formatTramsInvoiceDisplayNumber(tramsInvoiceNumber)}`}
                                onClick={() => setInquiryInvoiceId(invoice.id)}
                              >
                                <HelpCircle className="h-3.5 w-3.5 mr-1.5" />
                                Commission Inquiry
                              </Button>
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>Commission Inquiry</TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      <AlertDialog
        open={!!voidTarget}
        onOpenChange={(open) => {
          if (!open) {
            setVoidTarget(null);
            setVoidReason("");
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Void Invoice
              {/* AR-210 (TC-11.3): the dialog never said which invoice it voids. */}
              {voidTarget ? (
                <span className="sr-only"> {formatTramsInvoiceDisplayNumber(getTramsInvoiceNumberFromMetadata(voidTarget.metadata))}</span>
              ) : null}
            </AlertDialogTitle>
            <VoidInvoiceConfirmDescription
              supplierName={voidTarget?.supplierName}
              totalFare={voidTarget?.totalFare}
            />
          </AlertDialogHeader>
          <div className="space-y-2">
            <Label htmlFor="void-reason">Reason (optional)</Label>
            <Textarea
              id="void-reason"
              value={voidReason}
              onChange={(e) => setVoidReason(e.target.value)}
              placeholder="Why are you voiding this invoice?"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleVoidConfirm}
              disabled={voidInvoice.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {voidInvoice.isPending ? "Voiding..." : "Void Invoice"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <CommissionInquiryDialog
        open={inquiryInvoiceId != null}
        onOpenChange={(open) => {
          if (!open) setInquiryInvoiceId(null);
        }}
        invoiceId={inquiryInvoiceId}
      />
    </>
  );
}
